import { ref, reactive, computed } from '@vue/composition-api'
import eventsApi from '@/services/api/events'
import judgesApi from '@/services/api/judges'
import useFormAlert from '@/composables/useFormAlert'
import EventBus from '@/store/EventBus'
import { defaultEvent } from './useEventCreate'

// Backend stores "unlimited" as null (see StoreEventRequest::prepareForValidation), but on
// the edit form the field should show 0 rather than a blank input for that same "unlimited" state.
function normalizeEventForForm(e) {
  return {
    ...e,
    max_submissions: e.max_submissions != null ? e.max_submissions : 0
  }
}

export default function useEventEdit(draftId, eventId) {
  const eventTypes = ref([])
  const loadingTypes = ref(false)
  const loading = ref(false)
  const loadFailed = ref(false)

  const isCombined = ref(false)
  const brotherId = ref(null)
  const events = reactive([defaultEvent(), defaultEvent()])
  const sharedForm = reactive({
    declared_prize: '',
    judges_num: 1,
    is_conjunction: false,
    discount_price: '',
    sponsor_logo: null,
    sponsor_logo_url: null,
    judge_ids: []
  })

  const selectedJudges = ref([])
  const searchResults = ref([])
  const loadingJudges = ref(false)

  const availableJudges = computed(() => {
    const selectedIds = selectedJudges.value.map(j => j.id)
    const extra = searchResults.value.filter(j => !selectedIds.includes(j.id))
    return [...selectedJudges.value, ...extra]
  })

  const judgeIds = computed(() => selectedJudges.value.map(j => j.id))

  const errors = ref({})
  const submitting = ref(false)
  const deletingLogo = ref(false)
  const alert = useFormAlert()

  async function searchJudges(term) {
    loadingJudges.value = true
    try {
      const params = term != null && term !== '' ? { search: term } : {}
      const res = await judgesApi.availableForEvent(params)
      searchResults.value = res.data.data
    } catch (_err) {
      // global http-error interceptor handles display
    } finally {
      loadingJudges.value = false
    }
  }

  function updateJudgeSelection(ids) {
    selectedJudges.value = availableJudges.value.filter(j => ids.includes(j.id))
  }

  function restoreLegacySelectedJudges() {
    if (selectedJudges.value.length || !sharedForm.judge_ids.length) return
    selectedJudges.value = searchResults.value.filter(j => sharedForm.judge_ids.includes(j.id))
  }

  async function loadEventTypes() {
    loadingTypes.value = true
    try {
      const res = await eventsApi.eventTypes()
      eventTypes.value = res.data.data
    } catch (_err) {
      // global http-error interceptor handles display
    } finally {
      loadingTypes.value = false
    }
  }

  async function loadEvent() {
    loading.value = true
    try {
      const res = await eventsApi.getEdit(draftId, eventId)
      const data = res.data.data

      sharedForm.declared_prize = data.declared_prize != null ? data.declared_prize : ''
      sharedForm.judges_num = data.judges_num != null ? data.judges_num : 1
      sharedForm.is_conjunction = data.is_conjunction != null ? data.is_conjunction : false
      sharedForm.discount_price = data.discount_price != null ? data.discount_price : ''
      sharedForm.sponsor_logo = null
      sharedForm.sponsor_logo_url = data.sponsor_logo != null ? data.sponsor_logo : null

      brotherId.value = data.brother_id != null ? data.brother_id : null
      isCombined.value = data.events.length > 1

      data.events.forEach((e, i) => {
        Object.assign(events[i], defaultEvent(), normalizeEventForForm(e))
      })

      sharedForm.judge_ids = data.judge_ids != null ? data.judge_ids : []
      selectedJudges.value = data.judges != null ? data.judges : []
    } catch (err) {
      loadFailed.value = true
      const data = err.response != null ? err.response.data : null
      const msg = data != null && data.message != null ? data.message : 'Failed to load event data.'
      EventBus.$emit('http-error-persistent', msg)
    } finally {
      loading.value = false
    }
  }

  function applyTypeDefaults(index, typeId) {
    const found = eventTypes.value.find(t => t.id === typeId)
    if (!found) return
    const d = found.defaults
    Object.assign(events[index], {
      riders_min_age: d.riders_min_age,
      riders_max_age: d.riders_max_age,
      riders_sex: d.riders_sex,
      riders_maiden: d.riders_maiden,
      ...(d.has_rounds !== false && d.rounds !== 0 && { rounds: d.rounds }),
      ...(d.has_horse_restrictions !== false && {
        horses_min_age: d.horses_min_age,
        horses_max_age: d.horses_max_age,
        horses_class_1: d.horse_classes.includes(1),
        horses_class_2: d.horse_classes.includes(2),
        horses_class_3: d.horse_classes.includes(3),
        horses_class_4: d.horse_classes.includes(4),
        horses_class_5: d.horse_classes.includes(5)
      })
    })
  }

  function updateEvent(index, updated) {
    const oldTypeId = events[index].event_type_id
    Object.assign(events[index], updated)
    if (updated.event_type_id != null && updated.event_type_id !== oldTypeId) {
      applyTypeDefaults(index, updated.event_type_id)
    }
  }

  async function reloadEvent() {
    try {
      const res = await eventsApi.getEdit(draftId, eventId)
      const data = res.data.data

      sharedForm.declared_prize = data.declared_prize != null ? data.declared_prize : ''
      sharedForm.judges_num = data.judges_num != null ? data.judges_num : 1
      sharedForm.is_conjunction = data.is_conjunction != null ? data.is_conjunction : false
      sharedForm.discount_price = data.discount_price != null ? data.discount_price : ''
      sharedForm.sponsor_logo = null
      sharedForm.sponsor_logo_url = data.sponsor_logo != null ? data.sponsor_logo : null

      sharedForm.judge_ids = data.judge_ids != null ? data.judge_ids : []
      selectedJudges.value = data.judges != null ? data.judges : []
      brotherId.value = data.brother_id != null ? data.brother_id : null
      isCombined.value = data.events.length > 1

      data.events.forEach((e, i) => {
        Object.assign(events[i], defaultEvent(), normalizeEventForForm(e))
      })
    } catch (_err) {
      // silent — page already loaded, no need to block
    }
  }

  async function deleteSponsorLogo() {
    deletingLogo.value = true
    alert.clear()
    try {
      await eventsApi.deleteSponsorLogo(eventId)
      sharedForm.sponsor_logo_url = null
    } catch (err) {
      const data = err.response != null ? err.response.data : null
      const msg = data != null && data.message != null ? data.message : 'Failed to delete sponsor logo.'
      alert.showError(msg)
    } finally {
      deletingLogo.value = false
    }
  }

  async function submit() {
    submitting.value = true
    errors.value = {}
    alert.clear()

    try {
      const eventsPayload = isCombined.value ? [events[0], events[1]] : [events[0]]

      let payload
      if (sharedForm.sponsor_logo) {
        payload = new FormData()
        payload.append('_method', 'PUT')
        payload.append('declared_prize', sharedForm.declared_prize != null ? sharedForm.declared_prize : '')
        payload.append('judges_num', sharedForm.judges_num)
        payload.append('judge_ids', JSON.stringify(judgeIds.value))
        payload.append('is_conjunction', sharedForm.is_conjunction ? '1' : '0')
        if (sharedForm.discount_price) payload.append('discount_price', sharedForm.discount_price)
        payload.append('sponsor_logo', sharedForm.sponsor_logo)
        eventsPayload.forEach((e, i) => {
          Object.entries(e).forEach(([k, v]) => {
            let val = v
            if (v === true) val = '1'
            else if (v === false) val = '0'
            else if (v == null) val = ''
            payload.append(`events[${i}][${k}]`, val)
          })
        })
      } else {
        payload = {
          events: eventsPayload.map(e => ({ ...e })),
          declared_prize: sharedForm.declared_prize !== '' ? sharedForm.declared_prize : null,
          judges_num: sharedForm.judges_num,
          judge_ids: judgeIds.value,
          is_conjunction: sharedForm.is_conjunction,
          discount_price: sharedForm.discount_price !== '' ? sharedForm.discount_price : null
        }
      }

      const res = await eventsApi.update(draftId, eventId, payload)
      return res.data.message != null ? res.data.message : true
    } catch (err) {
      const data = err.response != null ? err.response.data : null
      if (data != null && data.errors != null) {
        errors.value = data.errors
        const msg = data.message != null ? data.message : 'Please fix the errors below.'
        alert.showError(msg)
      } else if (data != null && data.message != null) {
        alert.showError(data.message)
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  return {
    eventTypes,
    loadingTypes,
    loading,
    loadFailed,
    isCombined,
    brotherId,
    events,
    sharedForm,
    errors,
    submitting,
    deletingLogo,
    alert,
    availableJudges,
    loadingJudges,
    judgeIds,
    loadEventTypes,
    loadEvent,
    reloadEvent,
    deleteSponsorLogo,
    updateEvent,
    searchJudges,
    updateJudgeSelection,
    restoreLegacySelectedJudges,
    submit
  }
}



// WEBPACK FOOTER //
// ./src/composables/events/useEventEdit.js